import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { CouponDbModel } from '@models/coupon-db.model';
import { StripSubscriptionDetails } from '@models/stripe-token.model';
import { TranslateService } from '@ngx-translate/core';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { PaymentService } from '@services/feature/payment.service';
import { WidgetService } from '@services/feature/widget.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { PaymentIntent, StripeElementLocale, StripeElementsOptions } from '@stripe/stripe-js';
import { StripeElementsDirective, StripeFactoryService, StripePaymentElementComponent, StripeServiceInterface } from 'ngx-stripe';
import { LightDomProjectionDirective } from '@directives/light-dom-projection.directive';

const STRIPE_TEXT_LIMIT = 500;

@Component({
  selector: 'app-stripe',
  templateUrl: './stripe.component.html',
  styleUrls: ['./stripe.component.scss'],
  imports: [StripePaymentElementComponent, StripeElementsDirective, LightDomProjectionDirective]
})
export class StripeComponent implements OnInit {

  @ViewChild(StripePaymentElementComponent) paymentElement?: StripePaymentElementComponent;
  @ViewChild('stripeView') private stripeViewRef?: ElementRef<HTMLDivElement>;

  @Input() connectedAccountId: string;
  @Input() coupon: CouponDbModel;
  @Input() finalCouponDiscount: number;
  @Input() originalAmount: number;
  @Input() priceAfterCouponReduction: number;

  /**
   * When set to true, triggers the payment confirmation flow.
   * This is used to ensure localStorage updates complete before calling confirmPayment().
   */
  @Input() set storageComplete(value: boolean) {
    if (value && this.paymentIntent) {
      this.handlePayment();
    }
  }

  @Output() stripeSuccessfulEvent = new EventEmitter<string>();
  @Output() resetStripeTokenEvent = new EventEmitter<string>();
  @Output() stripElementLoadEvent = new EventEmitter<boolean>();

  private stripeFactoryService = inject(StripeFactoryService);

  protected stripe!: StripeServiceInterface;
  protected stripeLoading = true;
  protected errorMessage?: string;
  protected elementsOptions: StripeElementsOptions = {
    locale: 'en',
  };

  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private bookingService = inject(BookingService);
  private paymentService = inject(PaymentService);
  private customEventService = inject(CustomEventService);
  private widgetService = inject(WidgetService);
  private swRouteService = inject(SwRouteService);

  private subscriptionDetails = new StripSubscriptionDetails();
  private paymentIntent?: PaymentIntent;

  ngOnInit(): void {
    this.stripe = this.stripeFactoryService.create(environment.stripe.publishableKey, {
      stripeAccount: this.connectedAccountId
    });
    this.setLocale();
    this.createPaymentIntent();
    this.customEventService.stripePaymentFlowRequested
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updatePaymentMethod());
  }

  protected onReady(): void {
    this.stripeLoading = false;
    // A remount after a failed load must not leave the previous error banner sitting above a
    // form that now works.
    this.errorMessage = undefined;
    this.stripElementLoadEvent.emit(true);
  }

  protected onLoadError(event: any): void {
    LoggerService.warn('[stripe error]', event);
    // Clear the skeleton and surface the failure. Without this, any Stripe load error left the
    // ghost bars up forever with no message — the customer just saw an empty payment box.
    this.stripeLoading = false;
    this.errorMessage = (event as { error?: { message?: string } })?.error?.message
      || 'Failed to load the payment form. Please try again.';
    this.stripElementLoadEvent.emit(false);
  }

  private createPaymentIntent(): void {
    this.updateSubscriptionDetails();
    this.paymentService.createPaymentIntent(this.subscriptionDetails)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paymentIntent) => {
          this.paymentIntent = paymentIntent;
          this.subscriptionDetails.payment_intent_id = paymentIntent.id;
          this.elementsOptions.clientSecret = this.paymentIntent.client_secret;
        },
        error: error => this.handleError('Failed to initialize the payment form. Please try again.', error)
      });
  }

  private updatePaymentMethod(): void {
    if (!this.paymentElement?.elements) {
      this.handleError('Stripe is still loading. Please wait a moment and try again.');
      return;
    }
    if (!this.paymentIntent) {
      this.handleError('Failed to initialize the payment form. Please try again.');
      this.createPaymentIntent();
      return;
    }
    this.errorMessage = undefined;
    this.updateSubscriptionDetails();
    this.paymentService.updatePaymentIntent(this.subscriptionDetails)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.triggerStorageEvent(),
        error: (error) => this.handleError('Failed to update your payment details. Please try again.', error)
      });
  }

  /**
   * Triggers the storage event to store booking data before payment confirmation.
   * Parent component will set storageComplete=true when done, which triggers handlePayment().
   */
  private triggerStorageEvent(): void {
    if (this.paymentIntent) {
      // Emit event to trigger bookingItems storage in parent component
      this.customEventService.storeBookingItemsEvent.emit();
    }
  }

  private handlePayment(): void {
    if (!this.paymentElement?.elements) {
      this.handleError('Stripe is still loading. Please wait a moment and try again.');
      return;
    }

    this.confirmStripePayment();
  }

  private confirmStripePayment(): void {
    this.stripe.confirmPayment({
      elements: this.paymentElement.elements,
      confirmParams: {
        return_url: this.swRouteService.getStripeReturnUrl()
      },
      redirect: 'if_required'
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result.error) {
            this.errorMessage = result.error.message;
            this.customEventService.resetStripeTokenEvent.emit(result.error.message);
            LoggerService.error(result.error);
            this.scrollToStripeView();
            this.recreatePaymentIntent();
          } else if (result.paymentIntent) {
            LoggerService.log('[stripe] Payment successful:', result.paymentIntent);
            this.customEventService.stripeSuccessfulEvent.emit(result.paymentIntent.id);
          }
        },
        error: (error) => this.handleError('Payment confirmation failed. Please try again.', error)
      });
  }

  private setLocale(): void {
    this.elementsOptions.locale = this.translate.currentLang?.split('_')?.[0] as StripeElementLocale || 'de';
  }

  private updateSubscriptionDetails(): void {
    const partner = this.bookingService.partnerData;
    const selectedUser = this.bookingService.selectedBookingData?.selectedUser;
    const totalAmount = this.bookingService.selectedBookingData?.totalBookingAmount ?? 0;
    const bookedServices = this.getBookedServicesForStripe();

    this.subscriptionDetails.currency = partner?.currency || 'CHF';
    this.subscriptionDetails.amount = Math.round(totalAmount * 100);
    this.subscriptionDetails.connected_account_id = this.connectedAccountId;
    this.subscriptionDetails.automatic_payment_methods = { allow_redirects: 'always', enabled: true };
    if (this.paymentIntent) {
      this.subscriptionDetails.payment_intent_id = this.paymentIntent.id;
    }
    this.subscriptionDetails.metadata = {
      email: selectedUser?.eMail || '',
      prename: selectedUser?.forename || '',
      lastname: selectedUser?.lastName || '',
      phone: selectedUser?.phone || '',
      mobile: selectedUser?.mobile || '',
      partner_id: partner?.id || 0,
      partner_name: partner?.booking_name || '',
      ...bookedServices.metadata,
    };
    this.subscriptionDetails.description = bookedServices.description;
    if (this.coupon) {
      this.subscriptionDetails.metadata = {
        ...this.subscriptionDetails.metadata,
        coupon_code: this.coupon.code,
        original_amount: this.originalAmount,
        is_fixed_amount: this.coupon.is_fixed_amount,
        is_percentage: this.coupon.is_percentage,
        coupon_amount: this.coupon.fixed_amount,
        coupon_percentage: this.coupon.percentage,
        coupon_final_discount: this.finalCouponDiscount,
        coupon_discount_amount: this.priceAfterCouponReduction,
        coupon_id: this.coupon.id,
      };
    }
  }

  /**
   * What the customer is paying for, so the partner can identify the booking from Stripe
   * alone: the booked service names become the PaymentIntent description and a metadata pair.
   */
  private getBookedServicesForStripe(): { description?: string; metadata: Record<string, string> } {
    const names: string[] = [];
    const ids: string[] = [];

    this.bookingService.selectedBookingData?.cartItems?.forEach(cartItem => {
      if (cartItem.service?.name) {
        names.push(cartItem.service.name);
        ids.push(String(cartItem.service.id ?? cartItem.serviceId));
      }
    });

    const metadata: Record<string, string> = { widget_type: 'smart_widget' };
    if (names.length) {
      metadata['service_names'] = this.truncateForStripe(names.join(', '));
      metadata['service_ids'] = this.truncateForStripe(ids.join(','));
    }

    return {
      description: names.length ? this.truncateForStripe(names.join(', ')) : undefined,
      metadata,
    };
  }

  /**
   * Stripe caps every metadata value at 500 characters; the description gets the same cap so a
   * booking with many services can never fail the PaymentIntent call on length. Cut on a code
   * point, not a UTF-16 unit: a slice through an emoji leaves a lone surrogate that the backend's
   * JSON parser rejects, which would fail the whole PaymentIntent request.
   */
  private truncateForStripe(value: string): string {
    return Array.from(value).slice(0, STRIPE_TEXT_LIMIT).join('');
  }

  private scrollToStripeView(): void {
    this.stripeViewRef?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  private recreatePaymentIntent(): void {
    this.paymentIntent = undefined;
    this.subscriptionDetails.payment_intent_id = undefined;
    this.stripeLoading = true;
    this.errorMessage = undefined;
    this.createPaymentIntent();
  }

  private handleError(message: string, error?: unknown): void {
    LoggerService.error(error || message);
    this.errorMessage = message;
    this.customEventService.resetStripeTokenEvent.emit(message);
    this.stripeLoading = false;
  }
}
